import { consoleEvents } from '@pb/utils'
import { createReducer } from 'redux-act'

import getFirstMessageFromMessagesArray from 'helpers/getFirstMessageFromMessagesArray'

import actions, { setMiddlePageEmail } from './actions'
import initialState from './initialState'
import { AppState } from './types'

const showErrorModal = (state, { error, errorGroup }): AppState => ({
  ...state,
  errorModal: {
    error: {
      ...(getFirstMessageFromMessagesArray(error.data) || {}),
      errorGroup,
      showError: true,
    },
  },
})

const closeErrorModal = (state, payload): AppState => {
  if (state.errorModal.error.code === payload.code) {
    return {
      ...state,
      errorModal: {
        ...state.errorModal,
        error: {
          ...state.errorModal.error,
          showError: false,
        },
      },
    }
  }
  return state
}

const setXhrErrorCode = (state, { statusCode, errorCode }): AppState => ({
  ...state,
  xhrErrorCodes: {
    ...state.xhrErrorCodes,
    [statusCode]: { statusCode, errorCode },
  },
})

const resetXhrErrorCode = (state): AppState => ({
  ...state,
  xhrErrorCodes: initialState.xhrErrorCodes,
})

const setScreenType = (state, screenSize): AppState => ({
  ...state,
  screenSize,
})

const fetchSettingsStart = (state): AppState => ({
  ...state,
  isSettingsLoading: true,
})

const fetchSettingsSuccess = (state, payload): AppState => ({
  ...state,
  isSettingsLoading: false,
  settings: payload,
})

const fetchSettingsFail = (state, error): AppState => {
  consoleEvents.error('Failed to fetch settings', error)

  return {
    ...state,
    isSettingsLoading: false,
  }
}

const fetchConfigStart = (state): any => ({
  ...state,
  isConfigLoading: true,
})

const fetchConfigSuccess = (state, payload): any => ({
  ...state,
  isConfigLoading: false,
  config: payload,
})

const fetchConfigFail = (state, error): any => {
  consoleEvents.error('Failed to fetch config', error)

  return {
    ...state,
    isConfigLoading: false,
  }
}

const toggleLoaders = (state): AppState => ({
  ...state,
  showLoaders: !state.showLoaders,
})

const fetchUserStart = (state): AppState => ({
  ...state,
  isUserLoading: true,
})

const fetchUserSuccess = (state, user): AppState => ({
  ...state,
  isUserLoading: false,
  isUserLoadingFailed: false,
  isUserRequested: true,
  user,
  featureToggleFlags: {
    shouldFetchFeatureToggleFlags: true,
  },
})

const fetchUserFail = (state): AppState => ({
  ...state,
  isUserLoading: false,
  isUserLoadingFailed: true,
  isUserRequested: true,
})

const changeCustomerType = (state, customerType): AppState => ({
  ...state,
  user: {
    ...state.user,
    customerType,
  },
})

const changeCustomerStatus = (state, status): AppState => ({
  ...state,
  user: {
    ...state.user,
    status,
  },
})

const updateUser = (state, updatedUser): AppState => ({
  ...state,
  user: {
    ...state.user,
    ...updatedUser,
  },
})

const fetchToggleFlagsStart = (state): AppState => ({
  ...state,
  featureToggleFlags: { isLoading: true },
})

const fetchToggleFlagsSuccess = (state, featureToggleFlags): AppState => ({
  ...state,
  featureToggleFlags: { ...featureToggleFlags },
})

const fetchToggleFlagsFail = (state, payloadError): AppState => {
  const { response } = payloadError
  const error = getFirstMessageFromMessagesArray(response.data) || {}
  return {
    ...state,
    featureToggleFlags: { error },
  }
}

const setPreviousFNSubscribeState = (state: AppState, payload: boolean): AppState => ({
  ...state,
  previousFNSubscribeState: payload,
})

const setIsSendAgainClicked = (state: AppState, isSendAgainClicked: boolean): AppState => ({
  ...state,
  isSendAgainClicked,
})

const setIsSendAgainAlreadyClicked = (
  state: AppState,
  isSendAgainAlreadyClicked: boolean,
): AppState => ({
  ...state,
  isSendAgainAlreadyClicked,
})

const setIsChallengesBannerTrackingSent = (
  state: AppState,
  isChallengesBannerTrackingSent: boolean,
): AppState => ({
  ...state,
  isChallengesBannerTrackingSent,
})

const setMiddlePageEmailReducer = (state: AppState, middlePageEmail: string): AppState => ({
  ...state,
  middlePageEmail,
})

const reducer = createReducer((on) => {
  on(actions.setXhrErrorCode, setXhrErrorCode)
  on(actions.resetXhrErrorCode, resetXhrErrorCode)

  on(actions.setScreenType, setScreenType)

  on(actions.fetchSettingsStart, fetchSettingsStart)
  on(actions.fetchSettingsSuccess, fetchSettingsSuccess)
  on(actions.fetchSettingsFail, fetchSettingsFail)

  on(actions.fetchConfigStart, fetchConfigStart)
  on(actions.fetchConfigSuccess, fetchConfigSuccess)
  on(actions.fetchConfigFail, fetchConfigFail)

  on(actions.toggleLoaders, toggleLoaders)

  on(actions.fetchUserStart, fetchUserStart)
  on(actions.fetchUserSuccess, fetchUserSuccess)
  on(actions.fetchUserFail, fetchUserFail)

  on(actions.fetchToggleFlagsStart, fetchToggleFlagsStart)
  on(actions.fetchToggleFlagsSuccess, fetchToggleFlagsSuccess)
  on(actions.fetchToggleFlagsFail, fetchToggleFlagsFail)

  on(actions.changeCustomerType, changeCustomerType)
  on(actions.changeCustomerStatus, changeCustomerStatus)

  on(actions.updateUser, updateUser)

  on(actions.showErrorModal, showErrorModal)
  on(actions.closeErrorModal, closeErrorModal)

  on(actions.setPreviousFNSubscribeState, setPreviousFNSubscribeState)
  on(actions.setIsSendAgainClicked, setIsSendAgainClicked)
  on(actions.setIsSendAgainAlreadyClicked, setIsSendAgainAlreadyClicked)
  on(actions.setIsChallengesBannerTrackingSent, setIsChallengesBannerTrackingSent)
  on(setMiddlePageEmail, setMiddlePageEmailReducer)
}, initialState)

export default reducer
